import { useEffect, useRef, useState } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { saveShippingAddress, saveShippingMethod } from '../../slices/cartSlice';
import {
  DELIVERY_TIME_SLOTS,
  CARRIERS_WITH_TIME_SLOT,
  validateShippingContact,
} from '../../utils/shippingContact';

// Mandatory carrier contact info inside 運送資訊:
//   - 黑貓宅配 only: 配達時段 (saved on cart.shippingMethod.deliveryTime, so
//     switching to 中華郵政 drops it automatically)
//   - every carrier: Email + 手機 (saved on cart.shippingAddress)
// Same Redux -> localStorage flow as the address fields.
//
// Errors show after a field is left (blur) or when `showErrors` is true
// (the shopper pressed 送出訂單 with something missing).
const ShippingContact = ({ showErrors = false }) => {
  const dispatch = useDispatch();
  const { shippingAddress, shippingMethod } = useSelector((state) => state.cart);
  const { userInfo } = useSelector((state) => state.auth);
  const [touched, setTouched] = useState({});

  const needsSlot = CARRIERS_WITH_TIME_SLOT.includes(shippingMethod?.id);
  const errors = validateShippingContact({
    email: shippingAddress?.email,
    mobile: shippingAddress?.mobile,
    shippingMethodId: shippingMethod?.id,
    deliveryTime: shippingMethod?.deliveryTime,
  });
  const show = (field) => (showErrors || touched[field]) && errors[field];

  // Convenience: prefill the member's email + saved mobile once
  // (skip LINE placeholder emails; never overwrite what's already typed).
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current) return;
    prefilled.current = true;
    const memberEmail = userInfo?.email;
    const fill = {};
    if (!shippingAddress?.email && memberEmail && !/@users\.line\.local$/i.test(memberEmail)) {
      fill.email = memberEmail;
    }
    if (!shippingAddress?.mobile && userInfo?.mobile) {
      fill.mobile = userInfo.mobile;
    }
    if (Object.keys(fill).length > 0) {
      dispatch(saveShippingAddress({ ...shippingAddress, ...fill }));
    }
  }, [dispatch, shippingAddress, userInfo]);

  const onField = (field) => (e) =>
    dispatch(saveShippingAddress({ ...shippingAddress, [field]: e.target.value }));
  const onBlur = (field) => () => setTouched((t) => ({ ...t, [field]: true }));
  const pickSlot = (slot) => () =>
    dispatch(saveShippingMethod({ ...shippingMethod, deliveryTime: slot }));

  return (
    <>
      {needsSlot && (
        <fieldset className="mb-4">
          <legend className="op-subtitle h6">
            配達時段 <span className="op-required">*必填</span>
          </legend>
          <div className="op-slot-grid">
            {Object.entries(DELIVERY_TIME_SLOTS).map(([id, label]) => {
              const active = shippingMethod?.deliveryTime === id;
              return (
                <label
                  key={id}
                  htmlFor={`slot-${id}`}
                  className={`op-choice op-slot ${active ? 'is-active' : ''}`}
                >
                  <span className="op-choice-name flex-grow-1">{label}</span>
                  <input
                    type="radio"
                    className="form-check-input"
                    id={`slot-${id}`}
                    name="deliveryTime"
                    value={id}
                    checked={active}
                    onChange={pickSlot(id)}
                  />
                </label>
              );
            })}
          </div>
          {showErrors && errors.deliveryTime && (
            <div id="deliveryTime-error" className="op-field-error" role="alert">
              {errors.deliveryTime}
            </div>
          )}
        </fieldset>
      )}

      <h6 className="op-subtitle">
        聯絡資訊 <span className="op-required">*必填，物流業者通知用</span>
      </h6>
      <Row>
        <Col md={6}>
          <Form.Group controlId="contactEmail" className="my-2">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              autoComplete="email"
              placeholder="name@example.com"
              value={shippingAddress?.email || ''}
              onChange={onField('email')}
              onBlur={onBlur('email')}
              isInvalid={Boolean(show('email'))}
              required
            />
            <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="contactMobile" className="my-2">
            <Form.Label>手機號碼</Form.Label>
            <Form.Control
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              placeholder="0912345678"
              value={shippingAddress?.mobile || ''}
              onChange={onField('mobile')}
              onBlur={onBlur('mobile')}
              isInvalid={Boolean(show('mobile'))}
              required
            />
            <Form.Control.Feedback type="invalid">{errors.mobile}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
    </>
  );
};

export default ShippingContact;
